import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const cardVariants = cva(
  "relative rounded-xl border bg-card text-card-foreground transition-all duration-300 ease-out overflow-hidden",
  {
    variants: {
      variant: {
        default: "shadow-sm hover:shadow-lg hover:shadow-primary/5",
        glass: "backdrop-blur-xl bg-card/70 border-border/30 shadow-lg hover:shadow-xl hover:bg-card/80 hover:border-border/50",
        glow: "border-accent/20 shadow-lg shadow-accent/5 hover:shadow-accent/20 hover:shadow-xl hover:border-accent/40",
        float: "shadow-md hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10",
        premium: "border-border/40 bg-gradient-to-br from-card via-card to-muted/30 shadow-lg hover:shadow-xl hover:-translate-y-1 hover:border-accent/30 before:absolute before:inset-0 before:pointer-events-none before:bg-gradient-to-br before:from-accent/5 before:via-transparent before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-500",
        interactive: "shadow-sm hover:shadow-xl hover:-translate-y-1 hover:border-accent/30 active:translate-y-0 active:shadow-md cursor-pointer",
        subtle: "shadow-none border-border/50 hover:shadow-md hover:bg-muted/30 hover:border-border",
        shine: "shadow-md hover:shadow-xl before:absolute before:inset-0 before:bg-gradient-to-r before:from-transparent before:via-white/10 before:to-transparent before:-translate-x-full hover:before:translate-x-full before:transition-transform before:duration-700 before:ease-out",
        elevated: "shadow-xl shadow-primary/5 border-border/30 hover:shadow-2xl hover:shadow-primary/10 hover:-translate-y-0.5",
        gradient: "border-0 bg-gradient-to-br from-card via-card to-accent/5 shadow-lg hover:shadow-xl hover:to-accent/10",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

export interface CardProps 
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {
  shimmer?: boolean;
}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, shimmer, ...props }, ref) => (
    <div 
      ref={ref} 
      className={cn(
        cardVariants({ variant, className }),
        shimmer && "after:absolute after:inset-0 after:pointer-events-none after:bg-gradient-to-r after:from-transparent after:via-white/5 after:to-transparent after:-translate-x-full hover:after:translate-x-full after:transition-transform after:duration-1000 after:ease-out"
      )} 
      {...props} 
    />
  )
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants };
